import dayjs from "dayjs";
import { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Toast } from "../../../../../../components/toast/Toast";
import { useToast } from "../../../../../../components/toast/useToast.shared";
import { useOrderDocumentDownload } from "../../../../../../hooks/api/v2/orderDocument/useOrderDocumentDownload";
import { downloadPDF } from "../../../../../../utils/downloadHelpers";
import { Icon } from "@sellar-io/components";
import { StatementListResponseType } from "@sellar-io/api-v2/types";
import { DownloadDialog } from "../../../../../../components/newOrderSummary/Summary/OrderDocumentSummary";
import { useCreditProcessorDownloadOrderInvoice } from "../../../../../../hooks/api/v2/creditProcessor/useCreditProcessorDownloadOrderInvoice";
import { useSellarCurrentBusiness } from "../../../../../../hooks/useSellarCurrentBusiness.shared";

export const StatementDownloadButton = ({
  document,
  className,
}: {
  document: StatementListResponseType[number];
  className?: string;
}) => {
  const { business } = useSellarCurrentBusiness();
  const isBuyer = !!business?.Retailer;
  const orderDocumentDownloadMutation = useOrderDocumentDownload();
  const [isDownloadDialogActive, setIsDownloadDialogActive] = useState(false);
  const { toast } = useToast();
  const creditProcessorDownloadInvoiceMutation =
    useCreditProcessorDownloadOrderInvoice();

  const handleDownloadSellarDocument = ({
    document,
  }: {
    document: StatementListResponseType[number];
  }) => {
    setIsDownloadDialogActive(true);
    orderDocumentDownloadMutation.mutate(
      {
        id: document.id,
        type: document.type === "CN" ? "credit-note" : "invoice",
      },
      {
        onSuccess: (data) => {
          const documentName =
            document.type === "CN" ? "Credit note" : "Invoice";
          const documentFileName = `${documentName} - ${document.customId} - ${dayjs(
            document.issuedAt ?? new Date(),
          ).format("DD-MM-YYYY")}.pdf`;

          downloadPDF(documentFileName, data.document);
          setIsDownloadDialogActive(false);
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                Unable to download invoice at this time
              </Toast>
            ),
          });
          setIsDownloadDialogActive(false);
        },
      },
    );
  };

  const handleDownloadTwoDocument = ({ orderId }: { orderId: number }) => {
    setIsDownloadDialogActive(true);

    creditProcessorDownloadInvoiceMutation.mutate(
      {
        orderId,
      },
      {
        onSuccess: (data) => {
          const documentName = "Invoice";
          const documentFileName = `${documentName} - ${orderId}.pdf`;
          downloadPDF(documentFileName, data.document);
          setIsDownloadDialogActive(false);
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                Unable to download invoice at this time
              </Toast>
            ),
          });
          setIsDownloadDialogActive(false);
        },
      },
    );
  };

  const isTwoOrder = Boolean(document.TwoOrder?.id);

  return (
    <>
      <div
        className={twMerge(
          "cursor-pointer text-zinc-400 hover:text-zinc-700",
          className,
        )}
        onClick={() => {
          if (isTwoOrder && isBuyer) {
            return handleDownloadTwoDocument({
              orderId: document.OrderId ?? 0,
            });
          }
          return handleDownloadSellarDocument({ document });
        }}
      >
        <Icon icon="DOWNLOAD_ARROW" size={0.8} />
      </div>
      <DownloadDialog
        active={isDownloadDialogActive}
        onDismiss={() => setIsDownloadDialogActive(false)}
      />
    </>
  );
};
